import { useState, useRef } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';
import rrulePlugin from '@fullcalendar/rrule';
import { EventInput, EventClickArg } from '@fullcalendar/core';
import {
  Box,
  Typography,
  Button,
  ButtonGroup,
  Paper,
  Container,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import ViewDayIcon from '@mui/icons-material/ViewDay';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';
import './CalendarView.scss';

export default function CalendarView() {
  const theme = useTheme();
  const calendarRef = useRef<FullCalendar>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newEventTitle, setNewEventTitle] = useState('');

  const [events, setEvents] = useState<EventInput[]>([
    {
      id: '1',
      title: 'Regular Event',
      start: '2025-04-25T10:00:00',
      end: '2025-04-25T12:00:00',
      backgroundColor: theme.palette.primary.main,
      borderColor: theme.palette.primary.main,
    },
    {
      id: '2',
      title: 'All Day Event',
      start: '2025-04-24',
      allDay: true,
      backgroundColor: theme.palette.secondary.main,
      borderColor: theme.palette.secondary.main,
    },
    {
      id: '3',
      title: 'Weekly Recurring Meeting',
      rrule: {
        freq: 'weekly',
        dtstart: '2025-04-23T14:00:00',
        byweekday: ['we'],
        until: '2025-07-31',
      },
      duration: '01:00',
      backgroundColor: theme.palette.info.main,
      borderColor: theme.palette.info.main,
    },
  ]);

  const handleDateClick = () => {
    // More elegant handling with dialog instead of alert
    setDialogOpen(true);
    // Pre-fill with the clicked date
    // You could store the selected date in state here
  };

  const handleEventClick = (clickInfo: EventClickArg) => {
    // You could show an event detail dialog here instead of alert
    console.info('Event clicked:', clickInfo.event);
  };

  const handleAddEvent = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setNewEventTitle('');
  };

  const handleCreateEvent = () => {
    if (newEventTitle) {
      const newEvent: EventInput = {
        id: String(events.length + 1),
        title: newEventTitle,
        start: new Date().toISOString(),
        end: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString(),
        backgroundColor: theme.palette.primary.main,
        borderColor: theme.palette.primary.main,
      };
      setEvents([...events, newEvent]);
      handleDialogClose();
    }
  };

  const handleChangeView = (viewName: string) => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.changeView(viewName);
    }
  };

  return (
    <Container maxWidth='xl' sx={{ height: '100%', py: 2 }}>
      <Paper
        elevation={3}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          p: 3,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <Box sx={{ mb: 3 }}>
          <Typography variant='h4' component='h1' gutterBottom>
            Calendar
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Button
              variant='contained'
              startIcon={<AddIcon />}
              onClick={handleAddEvent}
            >
              Add Event
            </Button>

            <ButtonGroup variant='outlined'>
              <Button
                onClick={() => handleChangeView('dayGridMonth')}
                startIcon={<CalendarMonthIcon />}
              >
                Month
              </Button>
              <Button
                onClick={() => handleChangeView('timeGridWeek')}
                startIcon={<ViewWeekIcon />}
              >
                Week
              </Button>
              <Button
                onClick={() => handleChangeView('timeGridDay')}
                startIcon={<ViewDayIcon />}
              >
                Day
              </Button>
            </ButtonGroup>
          </Box>
        </Box>

        <Box sx={{ flexGrow: 1, height: '700px' }}>
          <FullCalendar
            ref={calendarRef}
            plugins={[
              dayGridPlugin,
              timeGridPlugin,
              interactionPlugin,
              momentTimezonePlugin,
              rrulePlugin,
            ]}
            initialView='dayGridMonth'
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth,timeGridWeek,timeGridDay',
            }}
            timeZone='America/New_York'
            events={events}
            editable={true}
            selectable={true}
            selectMirror={true}
            dayMaxEvents={true}
            weekends={true}
            dateClick={handleDateClick}
            eventClick={handleEventClick}
            eventTimeFormat={{
              hour: '2-digit',
              minute: '2-digit',
              meridiem: 'short',
            }}
            height='100%'
            // Custom styling options for better MUI integration
            themeSystem='standard'
            buttonText={{
              today: 'Today',
              month: 'Month',
              week: 'Week',
              day: 'Day',
            }}
            eventContent={(eventInfo) => (
              <Box
                sx={{
                  p: 0.5,
                  color: theme.palette.getContrastText(
                    (eventInfo.event.backgroundColor as string) ||
                      theme.palette.primary.main,
                  ),
                  fontSize: '0.85rem',
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  lineHeight: 1.2,
                }}
              >
                {eventInfo.timeText && (
                  <Box sx={{ fontSize: '0.75rem', opacity: 0.9, mb: 0.5 }}>
                    {eventInfo.timeText}
                  </Box>
                )}
                <Box>{eventInfo.event.title}</Box>
              </Box>
            )}
          />
        </Box>
      </Paper>

      {/* Add Event Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose}>
        <DialogTitle>Add New Event</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Enter the details for your new event.
          </DialogContentText>
          <TextField
            autoFocus
            margin='dense'
            id='name'
            label='Event Title'
            type='text'
            fullWidth
            variant='outlined'
            value={newEventTitle}
            onChange={(e) => setNewEventTitle(e.target.value)}
          />
          {/* You could add more fields here: date picker, time picker, etc. */}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleCreateEvent} variant='contained'>
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
